import { Icon, IconName, IconSize } from './Icon';

import clsx from 'clsx';
import React, { ButtonHTMLAttributes, FC } from 'react';
import { tv } from 'tailwind-variants';

export type IconButtonSize = 'xs' | 'sm' | 'md';
export type IconButtonVariant = 'primary' | 'secondary' | 'error' | 'accent' | 'text';

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'size'> {
  variant?: IconButtonVariant;
  size?: IconButtonSize;
  name: IconName;
  ariaLabel?: string;
  disabled?: boolean;
  outlined?: boolean;
  as?: React.ElementType;
  iconClassName?: string;
}

export const iconButtonTV = tv({
  base: 'inline-flex shrink-0 items-center justify-center outline-0 transition-all duration-300 ease-in-out focus:ring-1 focus:ring-other-outline focus:ring-offset-2',
  variants: {
    variant: {
      primary: 'bg-sem-d-surf-primary hover:bg-sem-l-surf-primary-hover active:bg-sem-l-surf-primary-pressed',
      secondary: 'bg-sem-l-surf-primary hover:bg-sem-l-surf-primary-hover active:bg-sem-l-surf-primary-pressed',
      error: 'bg-sem-d-surf-error hover:bg-sem-d-surf-error-hover active:bg-sem-d-surf-error-pressed',
      accent: 'active:bg-sem-d-surf-accent-pressed bg-sem-d-surf-accent hover:bg-sem-d-surf-accent-hover',
      text: 'text-sem-l-text-high hover:bg-sem-l-surf-primary-hover active:bg-sem-l-surf-primary-pressed',
    },
    size: {
      xs: 'size-6 rounded',
      sm: 'size-8 rounded-md',
      md: 'size-[42px] rounded-md',
    },
    outlined: {
      true: 'border border-sem-l-divider-medium',
    },
    disabled: {
      true: 'cursor-not-allowed bg-white text-sem-l-text-high-disabled hover:bg-white',
    },
  },
});

const iconTV = tv({
  variants: {
    variant: {
      primary: 'fill-sem-d-icon-high',
      secondary: 'fill-sem-l-icon-high',
      text: 'fill-sem-l-icon-high',
      'text-error': 'fill-prim-cranberry-70',
      error: 'fill-sem-d-icon-high',
      accent: 'fill-sem-d-icon-high',
    },
    disabled: {
      true: 'fill-sem-l-icon-high-disabled',
    },
  },
});

const getIconSize = (size: IconButtonSize): IconSize => {
  switch (size) {
    case 'xs':
      return '16';
    case 'sm':
      return '20';
    case 'md':
      return '20';
  }
};

export const IconButton: FC<IconButtonProps> = ({
  variant = 'secondary',
  size = 'xs',
  name,
  className = '',
  disabled,
  outlined,
  ariaLabel,
  as: Container = 'button',
  iconClassName,
  ...props
}) => {
  const iconButtonStyle = clsx(iconButtonTV({ variant, size, outlined, disabled }), className);
  const iconStyle = iconTV({ variant, disabled, className: iconClassName });
  const iconSize = getIconSize(size);
  return (
    <button type="button" className={iconButtonStyle} disabled={disabled} aria-label={ariaLabel || name} {...props}>
      <Icon name={name} size={iconSize} className={iconStyle} />
    </button>
  );
};
